import CrmOverviewTab from "./v2/CrmOverviewTab";
import {useTranslation} from "react-i18next";
import {useState} from "react";
import type {DateRange} from "../common/newDatePicker/datePickerShared";
import {createDefaultInsightsRange} from "./v2/insightsDateRangePresets";
import {
    RightSidebarActionButton,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../layout/rightSidebar/RightSidebarRail";
import {InsightsRailSearch, matchesTitle} from "./kit/InsightsRailSearch";
import InsightsDateRangeControl from "./v2/base/InsightsDateRangeControl";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import InsightsTargetsDialog from "./v2/targets/InsightsTargetsDialog";
import {useInsightTargets} from "./v2/targets/useInsightTargets";
import Badge from "@mui/material/Badge";
import EuroSymbolOutlinedIcon from "@mui/icons-material/EuroSymbolOutlined";
import {activeTargetPeriod, hasTargetValues} from "./v2/targets/targetResolution";

/** Same cap as the surveys insights page, so both modules centre their content identically. */
const CONTENT_MAX_WIDTH = 1440;

/**
 * Commercial performance for the CRM app. Owns the date range, the budgets dialog and the scroll
 * container; the body lives in `v2/CrmOverviewTab`. Demographics is its own tab.
 */
export function CrmInsightsTab() {
    const {t} = useTranslation();
    const [range, setRange] = useState<DateRange>(createDefaultInsightsRange);
    const [compareRange, setCompareRange] = useState<DateRange | null>(null);
    const [targetsDialogOpen, setTargetsDialogOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [isContentScrolled, setIsContentScrolled] = useState(false);
    const targetsQuery = useInsightTargets();
    const hasActiveTarget = hasTargetValues(activeTargetPeriod(targetsQuery.data));

    return (
        <>
            <RightSidebarRailLayout>
                <RightSidebarRailHeader>
                    <RightSidebarRailHeaderCenter>
                        <InsightsDateRangeControl
                            range={range}
                            compareRange={compareRange}
                            onChange={selection => {
                                setRange(selection.range);
                                setCompareRange(selection.compareRange);
                            }}
                        />
                    </RightSidebarRailHeaderCenter>
                </RightSidebarRailHeader>

                <Divider sx={{borderColor: isContentScrolled ? "divider" : "transparent"}}/>

                <Box
                    onScroll={event => setIsContentScrolled(event.currentTarget.scrollTop > 0)}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        overflowY: "auto",
                        pt: {xs: 2, md: 3},
                        pb: {xs: 6, md: 9},
                        px: {xs: 2, md: 3},
                    }}
                >
                    <Stack sx={{width: "100%", maxWidth: CONTENT_MAX_WIDTH, mx: "auto", minWidth: 0}}>
                        <CrmOverviewTab
                            range={range}
                            compareRange={compareRange}
                            matches={(title, key) => matchesTitle(query, title, key)}
                        />
                    </Stack>
                </Box>

                <RightSidebarRail>
                    <InsightsRailSearch query={query} onQueryChange={setQuery}/>

                    <RightSidebarActionButton
                        icon={(
                            <Badge
                                color="success"
                                variant="dot"
                                overlap="circular"
                                invisible={!hasActiveTarget}
                                sx={theme => ({
                                    "& .MuiBadge-badge": {
                                        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                                    },
                                })}
                            >
                                <EuroSymbolOutlinedIcon/>
                            </Badge>
                        )}
                        label={t("Manage budgets")}
                        onClick={() => setTargetsDialogOpen(true)}
                    />
                </RightSidebarRail>
            </RightSidebarRailLayout>

            <InsightsTargetsDialog
                open={targetsDialogOpen}
                onClose={() => setTargetsDialogOpen(false)}
            />
        </>
    );
}

export default CrmInsightsTab;
